import { getCategoryKey } from '@/storybook/components/FormCollection/utils/categoryOptions';
import { createSelectItemKey } from '@/storybook/components/FormCollection/utils/utils';
import { SelectItem } from '@/types/ui';
import { useVirtualizer, type VirtualItem } from '@tanstack/react-virtual';
import { measureElement as defaultMeasureElement } from '@tanstack/virtual-core';
import { useCallback, useMemo, useRef, type RefObject } from 'react';

export type VirtualRowKind = 'divider' | 'selected' | 'notSelected' | 'category';

export interface VirtualRowMeta<T> {
  kind: VirtualRowKind;
  option?: SelectItem<T>;
  category?: string;
  categoryKey?: string;
  indexWithinGroup?: number;
  isNoCategory?: boolean;
  isFirstCategory?: boolean;
  isCollapsed?: boolean;
}

export interface UseVirtualizedOptionsParams<T> {
  selectedOptions: SelectItem<T>[];
  notSelectedOptions: SelectItem<T>[];
  hasCategorizedOptions: boolean;
  categoriesToDisplay: Array<{ category: string; index: number }>;
  includeDivider: boolean;
  noCategoryLabel: string;
  threshold?: number;
  isCategoryCollapsed?: (categoryKey: string) => boolean;
}

/** Bundle passed to {@link VirtualizedRows} so callers do not thread each field separately. */
export type VirtualizedListState<T> = {
  virtualRowsData: VirtualRowMeta<T>[];
  shouldVirtualize: boolean;
  virtualItems: VirtualItem[];
  totalSize?: number;
  measureElement: (node: Element | null) => void;
};

export interface UseVirtualizedOptionsReturn<T> extends VirtualizedListState<T> {
  parentRef: RefObject<HTMLElement | null>;
  virtualizedList: VirtualizedListState<T>;
  getOptionKey: (meta: VirtualRowMeta<T>, idx: number) => string;
}

const DEFAULT_THRESHOLD = 80;

const EMPTY_VIRTUAL_ITEMS: VirtualItem[] = [];

export function useVirtualizedOptions<T>(params: UseVirtualizedOptionsParams<T>): UseVirtualizedOptionsReturn<T> {
  const {
    selectedOptions,
    notSelectedOptions,
    hasCategorizedOptions,
    categoriesToDisplay,
    includeDivider,
    noCategoryLabel,
    threshold = DEFAULT_THRESHOLD,
    isCategoryCollapsed,
  } = params;

  const { virtualRowsData, uncollapsedRowCount } = useMemo(() => {
    const rows: VirtualRowMeta<T>[] = [];
    const firstIndexByCategory = new Map(categoriesToDisplay.map(({ category, index }) => [category, index]));
    let isFirstCategory = true;
    let isCurrentCategoryCollapsed = false;
    let visibleNotSelectedIndex = 0;
    let collapsedOptionCount = 0;
    selectedOptions.forEach((opt, index) => rows.push({ kind: 'selected', option: opt, indexWithinGroup: index }));
    if (includeDivider) rows.push({ kind: 'divider' });
    notSelectedOptions.forEach((opt, index) => {
      const categoryKey = getCategoryKey(opt);
      const isFirstInCategory = hasCategorizedOptions && firstIndexByCategory.get(categoryKey) === index;
      if (isFirstInCategory) {
        isCurrentCategoryCollapsed = isCategoryCollapsed?.(categoryKey) ?? false;
        rows.push({
          kind: 'category',
          category: opt.category ?? noCategoryLabel,
          categoryKey,
          isNoCategory: opt.category == null,
          isFirstCategory,
          isCollapsed: isCurrentCategoryCollapsed,
        });
        isFirstCategory = false;
      }
      if (isCurrentCategoryCollapsed) {
        collapsedOptionCount += 1;
        return;
      }
      rows.push({ kind: 'notSelected', option: opt, indexWithinGroup: visibleNotSelectedIndex });
      visibleNotSelectedIndex += 1;
    });
    return { virtualRowsData: rows, uncollapsedRowCount: rows.length + collapsedOptionCount };
  }, [
    selectedOptions,
    notSelectedOptions,
    hasCategorizedOptions,
    categoriesToDisplay,
    includeDivider,
    noCategoryLabel,
    isCategoryCollapsed,
  ]);

  // Ignores collapse state so a header toggle never swaps render modes and remounts the focused row.
  const shouldVirtualize = uncollapsedRowCount > threshold;
  const parentRef = useRef<HTMLElement | null>(null);

  const virtualizer = useVirtualizer({
    count: shouldVirtualize ? virtualRowsData.length : 0,
    getScrollElement: () => parentRef.current,
    estimateSize: index => {
      const row = virtualRowsData[index];
      if (!row) return 64;
      if (row.kind === 'divider') return 1;
      if (row.kind === 'category') return 40;
      // TreeItem: line-clamp 2 + checkbox + padding — actual height 1 vs 2 lines is corrected via measureElement (see VirtualizedRows).
      return 64;
    },
    measureElement: defaultMeasureElement,
    overscan: 8,
  });

  const virtualItemsCandidate = shouldVirtualize ? virtualizer.getVirtualItems() : null;
  const virtualItems = useMemo(() => virtualItemsCandidate ?? EMPTY_VIRTUAL_ITEMS, [virtualItemsCandidate]);
  const totalSize = shouldVirtualize ? virtualizer.getTotalSize() : undefined;

  const measureElement = useCallback(
    (node: Element | null) => {
      virtualizer.measureElement(node);
    },
    [virtualizer],
  );

  const virtualizedList = useMemo(
    (): VirtualizedListState<T> => ({
      virtualRowsData,
      shouldVirtualize,
      virtualItems,
      totalSize,
      measureElement,
    }),
    [virtualRowsData, shouldVirtualize, virtualItems, totalSize, measureElement],
  );

  const getOptionKey = (meta: VirtualRowMeta<T>, idx: number): string => {
    switch (meta.kind) {
      case 'divider':
        return `virtual-divider-${idx}`;
      case 'category':
        return `virtual-category-${idx}-${meta.category}`;
      case 'selected':
      case 'notSelected':
        return `virtual-option-${idx}-${meta.option ? createSelectItemKey(meta.option) : 'unknown'}`;
      default:
        return `virtual-row-${idx}`;
    }
  };

  return {
    virtualRowsData,
    shouldVirtualize,
    parentRef,
    virtualItems,
    totalSize,
    measureElement,
    virtualizedList,
    getOptionKey,
  };
}

export default useVirtualizedOptions;
